<template>
  <div class="pb-6">
    <div class="flex h-full flex-col items-center gap-6">
      <!-- STRATEGY NAME -->
      <div class="flex flex-col items-center">
        <h1 class="text-ontick-steel-900 text-2xl font-semibold dark:text-gray-200">
          {{ data.investment.strategy_name }}
        </h1>
        <h2 class="text-ontick-steel-700 text-sm dark:text-gray-300">
          Ativo operado:
          {{
            typeof data.investment.strategy_symbol === "string"
              ? data.investment.strategy_symbol
              : data.investment.strategy_symbol.join(", ")
          }}
        </h2>
      </div>

      <!-- ACCOUNT INFO -->
      <div class="flex items-center justify-center gap-2">
        <ontick-broker-logo-component :broker="getSelectedAccount.broker_name" />
        <span class="text-ontick-steel-900 text-lg font-semibold dark:text-gray-200">
          {{ getSelectedAccount.broker_name }}
        </span>
        <span class="text-ontick-steel-400 text-lg font-medium dark:text-gray-400">
          (#{{ getSelectedAccount.account_number }})
        </span>
        <ontick-account-type-badge-component :accountType="getSelectedAccount.account_type" />
      </div>

      <p class="text-ontick-carbon-900 mb-6 text-center text-xl font-medium dark:text-gray-200">Parar investimento</p>

      <div class="text-ontick-carbon-900 dark:text-ontick-graphite-300">
        <p class="flex">
          <span class="text-ontick-green-500 pr-2">
            <svg
              class="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </span>
          <span>Todas as posições abertas serão fechadas.</span>
        </p>
        <p class="flex">
          <span class="text-ontick-green-500 pr-2">
            <svg
              class="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </span>
          <span>Todas as ordens pendentes serão canceladas.</span>
        </p>
      </div>

      <p v-if="capacity_warning" class="w-5/6 text-ontick-steel-900 text-center text-sm dark:text-ontick-yellow-700">
        Atenção: esta estratégia atingiu o limite máximo de usuários. Ao desligá-la, talvez não seja possível
        reativá-la. Deseja continuar?
      </p>

      <p class="text-ontick-steel-900 text-center text-sm dark:text-gray-300">
        Você está prestes a desligar este investimento
      </p>

      <div v-if="getSelectedAccount.account_type === 'PRD'" class="flex flex-col gap-6">
        <p class="text-sm">
          Digite "<span class="text-ontick-blue-600 font-bold">confirmar</span>" para parar o investimento
        </p>
        <input
          type="text"
          v-model="confirmation"
          class="dark:bg-ontick-carbon-500 border-ontick-steel-300 h-10 rounded border-2 bg-gray-100 px-2 text-center dark:border-0"
        />
      </div>

      <button
        :disabled="buttonDisabled"
        @click="stopInvestment"
        type="button"
        class="bg-ontick-red-700 hover:bg-ontick-red-600 inline-flex w-4/6 items-center justify-center rounded-md px-6 py-3 text-sm font-bold uppercase text-gray-50 shadow-lg transition-all duration-150 ease-in-out disabled:bg-gray-600 dark:text-gray-300"
      >
        <i v-html="$icons.loading" v-if="loadingStop"></i>
        <span class="uppercase" v-else>Parar estratégia</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OntickAccountTypeBadgeComponent from "../../../../../shared/components/basic/ontick-account-type-badge.component.vue";
import ontickBrokerLogoComponent from "../../../../../shared/components/basic/ontick-broker-logo.component.vue";

export default {
  components: {
    ontickBrokerLogoComponent,
    OntickAccountTypeBadgeComponent,
  },

  props: {
    data: {
      type: Object,
    },
  },

  data() {
    return {
      loadingStop: false,
      showSuccessMessage: false,
      capacity_warning: false,
      confirmation: "",
    };
  },

  computed: {
    ...mapGetters({
      getSelectedAccount: "accountTypes/getSelectedAccount",
    }),
    buttonDisabled() {
      if (this.getSelectedAccount.account_type === "PRD") {
        return this.confirmation.toUpperCase() !== "CONFIRMAR";
      }

      return false;
    },
  },

  methods: {
    ...mapActions({
      getInvestments: "investment/getInvestments",
    }),
    async stopInvestment() {
      this.loadingStop = true;
      try {
        const { data } = await this.$api.post(this.$endpoint.STOP_INVESTMENT, {
          investment_id: this.data.investment.investment_id,
          account_id: this.getSelectedAccount.account_id,
        });
        this.$swal("Sucesso", data.msg, "success");
        this.getInvestments();
      } catch (err) {
        console.error();
        this.$swal("Erro", err.data.msg, "error");
      } finally {
        this.loadingStop = false;
        this.$emit("close");
      }
    },
    async checkCapacity() {
      const params = {
        investment_id: this.data.investment.investment_id,
        account_id: this.getSelectedAccount.account_id,
      };

      try {
        const { data } = await this.$api.get(this.$endpoint.INVESTMENTS_CHECK_CAPACITY, { params });
        this.capacity_warning = data.capacity_warning;
      } catch {}
    },
  },

  created() {
    if (this.getSelectedAccount.account_type === "PRD") {
      this.checkCapacity();
    }
  },
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to
/* .component-fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
